import { Permisos } from '@/shared/constants/permisos';
import type { TourDefinicion } from '@/features/guia-interactiva/types/guia-interactiva.types';

/**
 * Tour contextual del editor de una tecnología. Auto-dispara la primera vez
 * que el usuario entra a CUALQUIER tecnología (independiente del id), y se
 * marca como visto en `toursVistos` al completarlo o cerrarlo. Los pasos
 * cuyo `permisoRequerido` no tiene el usuario se filtran automáticamente;
 * los que apuntan a elementos condicionales por estado (ej. el panel
 * 'Control de cambios' solo visible en pendiente_aprobacion para el
 * aprobador) se saltean si no están en el DOM en ese momento.
 */
export const tourRecetTecnologiaEditor: TourDefinicion = {
  codigo: 'recet-tecnologia-editor',
  titulo: 'Editor de tecnología',
  descripcion:
    'Recorrido por el editor: secciones, contenido, acciones de workflow, comentarios y control de cambios.',
  // Sin `ruta`: el tour vive en `/admin/recet/tecnologias/$id` (paramétrica).
  // lanzarTour evita navegar y corre el tour en la pantalla donde se disparó.
  // Por eso se oculta del índice global: si se lanzara desde otra pantalla
  // fallaría silenciosamente al no encontrar sus selectores.
  permisoMenu: Permisos.TECNOLOGIAS_VER,
  soloContextual: true,
  pasos: [
    {
      id: 'recet-tecnologia-editor:titulo',
      selector: '[data-testid="recet-tecnologia-editor.titulo"]',
      titulo: 'Título y estado',
      descripcion:
        'Edita el título y mira al lado el estado actual (borrador, pendiente de aprobación, publicada). El estado determina qué acciones están disponibles.',
      side: 'bottom',
      align: 'start',
    },
    {
      id: 'recet-tecnologia-editor:sidebar-secciones',
      selector: '[data-testid="recet-tecnologia-editor.sidebar-secciones"]',
      titulo: 'Secciones',
      descripcion:
        'Navega entre las secciones de la tecnología. El punto amarillo marca cambios o comentarios pendientes; el check verde, sección lista.',
      side: 'right',
      align: 'start',
    },
    {
      id: 'recet-tecnologia-editor:editor',
      selector: '[data-testid="recet-tecnologia-editor.editor"]',
      titulo: 'Área de edición',
      descripcion:
        'Escribe el contenido de la sección. Si tienes permiso de edición verás además la barra de formato (negrita, listas, enlaces, referencias, comentarios). Los cambios se autoguardan.',
      side: 'top',
      align: 'center',
    },
    {
      id: 'recet-tecnologia-editor:acciones',
      selector: '[data-testid="recet-tecnologia-editor.acciones"]',
      titulo: 'Acciones de la tecnología',
      descripcion:
        'Desde aquí solicitas aprobación, publicas, rechazas o retiras una solicitud según tu rol y el estado actual. También están los botones para descargar el original, exportar o eliminar.',
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor:tab-comentarios',
      selector: '[data-testid="recet-tecnologia-editor.tab-comentarios"]',
      titulo: 'Paneles laterales',
      descripcion:
        'Abre los paneles laterales para ver comentarios de la sección, control de cambios (sugerencias), referencias bibliográficas o el historial de versiones.',
      permisoRequerido: Permisos.COMENTARIOS_VER,
      side: 'bottom',
      align: 'end',
    },
  ],
};
